import {getWecomSdkSign, getWeChatConfig} from '@/api/wechat';
import * as ww from '@wecom/jssdk';
import {getPhysicianWechatDetail, logContentSend} from '@/api/content';
import { s3Service } from '@/utils/aws';
import { Dialog } from 'vant';

// 自定停止录音提前时间，防止未能调用停止录音时间获取语音文件失败
export const RECORD_AUTOSTOP_AHEAD_TIME = 0;
const FILE_SIZE_PROBE_TIMEOUT_MS = 5000;

class WecomSDK {
  constructor() {
    this.isMock = false;
    this.fileSizeCache = new Map();
    this.registeredUrl = '';
    this.registrationPromise = null;
    this.registrationUrl = '';
  }

  async register() {
    // 检查是否在企业微信环境
    const isWecom = /wxwork/.test(navigator.userAgent.toLowerCase());
    if (!isWecom) {
      console.log('Not in Wecom environment, using mock mode');
      this.isMock = true;
      return Promise.resolve();
    }

    const currentUrl = window.location.href.split('#')[0];
    if (this.registered && this.ww && this.registeredUrl === currentUrl) {
      return Promise.resolve();
    }

    if (this.registrationPromise) {
      if (this.registrationUrl === currentUrl) {
        return this.registrationPromise;
      }

      try {
        await this.registrationPromise;
      } catch (e) {
        // Retry below with a signature for the current URL.
      }
      return this.register();
    }

    this.registrationUrl = currentUrl;
    this.registrationPromise = this.registerUrl(currentUrl);

    try {
      await this.registrationPromise;
    } finally {
      if (this.registrationUrl === currentUrl) {
        this.registrationPromise = null;
        this.registrationUrl = '';
      }
    }
  }

  async registerUrl(currentUrl) {
    const sign = await getWecomSdkSign(currentUrl);
    console.log(sign, 'sign');

    this.ww = ww;
    return new Promise((resolve, reject) => {
      this.ww.register({
        corpId: sign.appid,
        getConfigSignature,
        onConfigSuccess: () => {
          console.log('onConfigSuccess');
          this.registered = true;
          this.registeredUrl = currentUrl;
          resolve();
        },
        onConfigFail: (err) => {
          console.error('onConfigFail:', err);
          reject(err);
        },
        onConfigComplete: (res) => {
          console.log('onConfigComplete:', res);
        },
        jsApiList: [
          'startRecord',
          'stopRecord',
          'onVoiceRecordEnd',
          'playVoice',
          'pauseVoice',
          'stopVoice',
          'uploadVoice',
          'downloadVoice',
          'translateVoice',
          'shareWechatMessage',
          'setClipboardData',
          'previewFile',
        ],
      });
    });

    async function getConfigSignature() {
      return {
        timestamp: sign.timestamp,
        nonceStr: sign.noncestr,
        signature: sign.sign,
      };
    }
  }

  stopRecord() {
    clearTimeout(this.recordEndTimer);

    if (this.isMock) {
      console.log('Mock stopRecord');
      return Promise.resolve({ localId: 'mock_local_id_' + Date.now() });
    }

    return this.ww.stopRecord({
      success: function (e) {
        this.localId = e.localId;
      },
    });
  }

  translateVoice(localId) {
    if (this.isMock) {
      console.log('Mock translateVoice', localId);
      return Promise.resolve({ translateResult: '这是一个模拟的语音输入内容，用于测试非企业微信环境下的功能。' });
    }
    return this.ww.translateVoice({
      localId,
      isShowProgressTips: true,
    });
  }

  playVoice(localId) {
    if (this.isMock) {
      console.log('Mock playVoice', localId);
      Dialog.alert({ message: '模拟播放语音: ' + localId });
      return Promise.resolve();
    }
    return this.ww.playVoice({
      localId,
    });
  }

  stopVoice(localId) {
    if (this.isMock) {
      console.log('Mock stopVoice', localId);
      return Promise.resolve();
    }
    return this.ww.stopVoice({
      localId,
    });
  }

  uploadVoice(localId) {
    if (this.isMock) {
      console.log('Mock uploadVoice', localId);
      return Promise.resolve({ serverId: 'mock_server_id_' + Date.now() });
    }
    return this.ww.uploadVoice({
      localId,
      isShowProgressTips: 1,
    });
  }

  startRecord({onVoiceRecordEnd} = {}) {
    if (this.isMock) {
      console.log('Mock startRecord');
      this.recordEnded = false;
      if (typeof onVoiceRecordEnd === 'function') {
        this.recordEndTimer = setTimeout(() => {
          onVoiceRecordEnd();
        }, 60000); // Mock auto stop in 60s
      }
      return Promise.resolve();
    }

    const result = this.ww.startRecord();
    this.recordEnded = false;

    // Use SDK onVoiceRecordEnd instead of setTimeout
    this.ww.onVoiceRecordEnd({
      complete: (res) => {
        console.log('onVoiceRecordEnd triggered', res);
        if (typeof onVoiceRecordEnd === 'function') {
          onVoiceRecordEnd(res);
        }
      }
    });

    // if (typeof onVoiceRecordEnd === 'function') {
    //   this.recordEndTimer = setTimeout(() => {
    //     onVoiceRecordEnd();
    //   }, 60000 - RECORD_AUTOSTOP_AHEAD_TIME);
    // }

    return result;
  }

  async shareWechatMessage(data) {
    try {
      // 获取医生微信详情
      const res = await getPhysicianWechatDetail(data.receiver_physician_id);
      let openid = null;

      if (res && res.data && res.data.open_id) {
        openid = res.data.open_id;
      }

      // 如果 openid 为空，弹出提示框
      if (!openid) {
        const confirmed = await this.showBindDialog();
        if (!confirmed) {
          console.log('用户取消了发送操作');
          return false; // 用户取消，不再继续发送
        }
      }

      // 准备发送日志参数
      const params = {
        content_id: data.content_id,
        sender_user_id: data.sender_user_id,
        sender_user_name: data.sender_user_name,
        sender_territory_id: data.sender_territory_id,
        receiver_physician_id: data.receiver_physician_id,
        receiver_openid: openid || undefined,
        // send_time: data.send_time,
      };

      // 记录分享日志
      const logRes = await logContentSend(params);
      if (!logRes.isSuccess || !logRes.data) {
        throw new Error('Failed to log content send.');
      }

      // 构建分享链接
      const contentUrl = `${window.location.origin}/public/open?send_id=${logRes.data}`;
      const wxLogo = data.wx_logo || 'https://hcp360-mobile.lundbecksys.cn/image/lundbeck-logo.png';
      console.log(contentUrl, {
        title: data.title,
        desc: data.description ?? '灵北同事给您发送了一篇文章',
        link: contentUrl,
        imgUrl: wxLogo,
      });

      // 确保 Wecom SDK 已注册
      await this.register();

      if (this.isMock) {
        console.log('Mock shareWechatMessage success', { title: data.title, link: contentUrl });
        return true;
      }

      // 调用分享接口
      await this.ww.shareWechatMessage({
        title: data.title,
        desc: data.description ?? '灵北同事给您发送了一篇文章',
        link: contentUrl,
        imgUrl: wxLogo,
        // success: () => {
        //   console.log('Share success');
        //   resolve();
        // },
        // fail: (err) => {
        //   console.error('Share failed:', err);
        //   reject(err);
        // },
      });

      return true;
    } catch (error) {
      console.error('Error in shareWechatMessage:', error);
      throw error; // 抛出错误，供调用者捕获
    }
  }

  async shareQrCode(data) {
    try {
      // 构建分享链接
      const contentUrl = `${window.location.origin}/api/weixin/Wechat/GetQRCode?scene=12345&expire_seconds=259200`;
      console.log(contentUrl);

      // 确保 Wecom SDK 已注册
      await this.register();

      // 调用分享接口
      this.ww.shareWechatMessage({
        title: '灵北邀请',
        desc: '灵北同事邀请您关注公众号',
        link: contentUrl,
        imgUrl: 'https://hcp360-mobile.lundbecksys.cn/image/lundbeck-logo.png',
        // success: () => {
        //   console.log('Share success');
        //   resolve();
        // },
        // fail: (err) => {
        //   console.error('Share failed:', err);
        //   reject(err);
        // },
      });
    } catch (error) {
      console.error('Error in shareWechatMessage:', error);
      throw error; // 抛出错误，供调用者捕获
    }
  }

  isWecomMobileEnvironment() {
    if (typeof navigator === 'undefined') return false;
    const userAgent = navigator.userAgent || '';
    return /wxwork/i.test(userAgent) && /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
  }

  async getRemoteFileSize(url) {
    let cacheKey = url;
    try {
      const parsedUrl = new URL(url);
      const expires = parsedUrl.searchParams.get('Expires') || '';
      cacheKey = `${parsedUrl.origin}${parsedUrl.pathname}?Expires=${expires}`;
    } catch (e) {
      // Use the original URL as cache key when parsing fails.
    }

    if (this.fileSizeCache.has(cacheKey)) {
      return this.fileSizeCache.get(cacheKey);
    }

    const controller = typeof window !== 'undefined' && window.AbortController
      ? new window.AbortController()
      : null;
    let timeoutId;

    try {
      const request = fetch(url, {
        method: 'GET',
        headers: {
          Range: 'bytes=0-0',
        },
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        ...(controller ? { signal: controller.signal } : {}),
      });
      const timeout = new Promise(resolve => {
        timeoutId = setTimeout(() => {
          if (controller) controller.abort();
          resolve(null);
        }, FILE_SIZE_PROBE_TIMEOUT_MS);
      });
      const response = await Promise.race([request, timeout]);
      if (!response) throw new Error('File size probe timed out');
      const contentRange = response.headers.get('content-range');
      const contentRangeMatch = contentRange && contentRange.match(/\/(\d+)$/);
      const contentLength = response.status === 200
        ? Number(response.headers.get('content-length'))
        : 0;
      const fileSize = contentRangeMatch ? Number(contentRangeMatch[1]) : contentLength;

      if (response.body && typeof response.body.cancel === 'function') {
        response.body.cancel().catch(() => {});
      }

      if (!response.ok || !Number.isSafeInteger(fileSize) || fileSize <= 0) {
        throw new Error('Unable to determine file size');
      }

      this.fileSizeCache.set(cacheKey, fileSize);
      return fileSize;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async previewDocument({ url, name, size } = {}) {
    if (!url || !this.isWecomMobileEnvironment()) return false;

    try {
      await this.register();
      if (this.isMock || !this.ww || typeof this.ww.previewFile !== 'function') return false;

      const providedSize = Number(size);
      const fileSize = Number.isSafeInteger(providedSize) && providedSize > 0
        ? providedSize
        : await this.getRemoteFileSize(url);

      await this.ww.previewFile({
        url,
        name,
        size: fileSize,
      });
      return true;
    } catch {
      console.warn('WeCom file preview unavailable, using browser fallback.');
      return false;
    }
  }

  async showBindDialog() {
    return new Promise((resolve) => {
      Dialog.confirm({
        title: '医生未绑定微信',
        message:
          '您选择的医生尚未绑定微信账号。本次发送的内容将在医生首次打开时，触发微信绑定与知情同意的签署流程。请在下一步确保选择该医生所对应的微信好友进行发送。',
        confirmButtonText: '确认',
        cancelButtonText: '取消',
      })
        .then(() => {
          resolve(true); // 用户点击确认
        })
        .catch(() => {
          resolve(false); // 用户点击取消
        });
    });
  }

  generateFollowLinkWithParams(officialAccountId, scene) {
    return `https://mp.weixin.qq.com/mp/profile_ext?action=home&__biz=${officialAccountId}&scene=${scene}#wechat_redirect`;
  }

  /**
   * 复制文本到剪贴板
   * @param {string} text - 要复制的文本
   * @returns {Promise<boolean>} - 是否复制成功
   */
  async copyToClipboard(text) {
    try {
      await this.register();
      console.log('Attempting to copy text:', text);

      if (this.ww && typeof this.ww.setClipboardData === 'function') {
        return new Promise((resolve, reject) => {
          this.ww.setClipboardData({
            data: text,
            success: () => {
              console.log('Copy success in Wecom');
              resolve(true);
            },
            fail: (err) => {
              console.error('Copy failed in Wecom:', err);
              this.fallbackCopy(text).then(resolve).catch(reject);
            }
          });
        });
      }

      return this.fallbackCopy(text);
    } catch (error) {
      console.error('Error in copyToClipboard:', error);
      return this.fallbackCopy(text);
    }
  }

  /**
   * 使用原生方法复制文本
   * @private
   */
  async fallbackCopy(text) {
    try {
      // 优先使用 navigator.clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // 降级方案：创建临时输入框
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      const success = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (!success) {
        throw new Error('Copy command was unsuccessful');
      }

      return true;
    } catch (error) {
      console.error('Fallback copy failed:', error);
      return false;
    }
  }
}

export const wecomSDK = new WecomSDK();
